import {
  DataType,
  Form,
  FormOptions,
  FormSkeleton,
  Section,
  SelectOption
} from '@globaleris/ui.library'
import { Box, Paper } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumTipoCantidad } from '../../../../api'
import { EnumPriceCalculationType } from '../../../../api/Argentia/enums'
import { getCustomerDomain } from '../../../../redux'
import { measureUnitToSelectOptions } from '../../flexibility.mapper'
import { LimitForm } from '../../flexibility.Model'
import { fetchMeasureUnitPowerType } from '../../flexibility.service'

interface FlexibilityPriceProps {
  quantityTypeOptions: SelectOption[]
  isFetchingTypesOptions: boolean
  limit?: LimitForm
  onChangeInput?: (input: 'quantityType' | 'percentage' | 'measureUnit', value: any) => void
  subtitle?: string
  children: React.ReactNode
  qtyIsNull?: boolean // BUG #51898 added to hide all fields when type Nulo is selected
  isFlexGroup?: boolean
}

export const FlexibilityLimitQuantity: React.FC<FlexibilityPriceProps> = ({
  quantityTypeOptions = [],
  isFetchingTypesOptions,
  limit,
  onChangeInput,
  subtitle,
  children,
  qtyIsNull,
  isFlexGroup
}) => {
  const [t] = useTranslation(['flexibilityDetails', 'globalerisLibrary', 'enums'])

  const [isFetchingMeasureOptions, setIsFetchingMeasureOptions] = useState<boolean>(false)
  const [measureOptions, setMeasureOptions] = useState<SelectOption[]>([])

  if (!limit) {
    return <FormSkeleton columns={3} inputs={6} />
  }

  const quantityLimit: LimitForm = {
    ...limit,
    quantityType: limit.quantityType !== undefined ? limit.quantityType : 9999,
    percentage: limit.percentage ? limit.percentage * 100 : limit.percentage
  }

  const isFlexGroupContractual =
    isFlexGroup &&
    (quantityLimit.quantityType === EnumTipoCantidad.Capacidad_Contractual ||
      quantityLimit.quantityType === EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion)

  //Form config Starts
  const options: FormOptions = {
    columns: 3,
    elevation: 0,
    analytics: {
      customer: getCustomerDomain(),
      view: t('flexibilityAdd:forms.add.analytics')
    }
  }

  const onChange = (input: 'quantityType' | 'percentage' | 'measureUnit', value) => {
    onChangeInput && onChangeInput(input, value)
  }

  const limitFormSections: Section[] = [
    {
      // title: t('flexibilityDetails:quantityLimit.title'),
      inputs: [
        {
          name: 'quantityType',
          label: t('flexibilityDetails:quantityLimit.inputs.quantityType'),
          type: DataType.SELECT,
          selectOptions: quantityTypeOptions,
          isLoading: isFetchingTypesOptions,
          required: true,
          onChange: (value, formState) => {
            onChange('quantityType', value)
          }
        },
        {
          name: 'percentage',
          label: t('flexibilityDetails:quantityLimit.inputs.percentage'),
          type: DataType.NUMBER,
          minValue: 0,
          maxValue: 100,
          clearable: true,
          required:
            limit.quantityType === EnumTipoCantidad.Libre ||
            limit.quantityType === EnumTipoCantidad.Libre_sin_Fijacion
              ? false
              : true,
          display:
            !!quantityLimit.quantityType &&
            quantityLimit.quantityType != 0 &&
            quantityLimit.quantityType != 9999 &&
            !qtyIsNull,
          onChange: (value, formState) => {
            onChange('percentage', typeof value === 'number' ? +value / 100 : null)
          }
        },
        {
          name: 'measureUnit',
          label: t('flexibilityDetails:quantityLimit.inputs.measureUnit'),
          type: DataType.SELECT,
          isLoading: isFetchingMeasureOptions,
          selectOptions: measureOptions,
          required: !isFlexGroupContractual,
          readOnly:
            quantityLimit.quantityType === EnumTipoCantidad.Capacidad_Contractual ||
            quantityLimit.quantityType === EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion,
          display:
            !isFlexGroupContractual && // If it's within a Flex Group and the quantity type is Contractual, then it's hidden
            !!quantityLimit.quantityType &&
            quantityLimit.quantityType != 0 &&
            quantityLimit.quantityType != 9999 &&
            !qtyIsNull &&
            (quantityLimit.quantityType === EnumTipoCantidad.Capacidad_Contractual ||
              quantityLimit.quantityType === EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion ||
              quantityLimit.quantityType === EnumTipoCantidad.Libre ||
              quantityLimit.quantityType === EnumTipoCantidad.Libre_sin_Fijacion),
          onChange: (value, formState) => {
            onChange('measureUnit', value)
          }
        }
      ]
    }
  ]

  const fetchMeasureOptions = async () => {
    setIsFetchingMeasureOptions(true)
    await fetchMeasureUnitPowerType()
      .then(response => {
        if (response.success) {
          setMeasureOptions(measureUnitToSelectOptions(response.data ?? []))
        }
      })
      .finally(() => setIsFetchingMeasureOptions(false))
  }

  useEffect(() => {
    fetchMeasureOptions()
  }, [])

  return (
    <>
      <Box marginTop={2}>
        <Paper elevation={3} style={{ padding: '8px' }}>
          <Form
            title={t('flexibilityDetails:quantityLimit.title')}
            subtitle={subtitle}
            sections={limitFormSections}
            options={options}
            data={quantityLimit}
          />
          <Box marginX={2} marginBottom={2}>
            {isFlexGroupContractual
              ? t('flexibilityDetails:quantityLimit.groupFlexMessage')
              : children}
          </Box>
        </Paper>
      </Box>
    </>
  )
}
